import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import services from '../../services';
import { BiArrowBack, BiTrash, BiUser, BiLayout } from 'react-icons/bi';
import { toast } from 'react-toastify';
import DeletePopup from '../DeletePopup';
import './../UserManage/User.css';

const BoardAllocation = () => {
    const [boardData, setBoardData] = useState([]);
    const [selectedBoard, setSelectedBoard] = useState(null);
    const [boardUsers, setBoardUsers] = useState([]);
    const [loader, setLoader] = useState(false);
    const [detailLoader, setDetailLoader] = useState(false);

    // Modal states
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [deleteInfo, setDeleteInfo] = useState(null); // { assignmentIds: [], userName: '' }

    const navigate = useNavigate();

    const fetchBoardSummary = async () => {
        setLoader(true);
        try {
            const [boardsRes, assignmentsRes] = await Promise.all([
                services.board.GETALLBOARD(),
                services.userassignboard.GET_ALL_UserAssignBoard()
            ]);

            const allBoards = boardsRes?.data || [];
            const data = Array.isArray(assignmentsRes) ? assignmentsRes : (assignmentsRes?.data || []);

            // Count UNIQUE users per board
            const boardUserMap = data.reduce((acc, curr) => {
                const boardId = curr.boardId;
                const userId = curr.userId;
                if (!boardId || !userId) return acc;
                if (!acc[boardId]) acc[boardId] = new Set();
                acc[boardId].add(userId);
                return acc;
            }, {});

            const summary = allBoards.map(board => ({
                id: board.id,
                name: board.boardName,
                userCount: boardUserMap[board.id]?.size || 0
            })).sort((a, b) => b.userCount - a.userCount);

            setBoardData(summary);
        } catch (error) {
            console.error('Error fetching board summary:', error);
            toast.error('Failed to fetch board summary');
        } finally {
            setLoader(false);
        }
    };

    const fetchBoardDetails = async (board) => {
        setDetailLoader(true);
        setSelectedBoard(board);
        try {
            const response = await services.userassignboard.GET_ASSIGN_USER_BY_BOARD_ID(board.id, '', 'true');
            const data = response?.data?.data || [];

            // Group assignments for selected board by userId (Deduplication)
            const groupedUsers = data.reduce((acc, item) => {
                const userId = item.userId;
                const user = item.User;
                if (!userId) return acc;

                if (!acc[userId]) {
                    acc[userId] = {
                        userId: userId,
                        name: user?.name || 'Unknown User',
                        email: user?.email || 'Unknown Email',
                        assignmentIds: [],
                        columns: new Set()
                    };
                }

                acc[userId].assignmentIds.push(item.id);

                try {
                    const parsed = typeof item.boardListId === 'string' ? JSON.parse(item.boardListId) : item.boardListId;
                    const columns = (Array.isArray(parsed) ? parsed : []).map(c => c.coloum || c.label).filter(Boolean);
                    columns.forEach(col => acc[userId].columns.add(col));
                } catch (e) {
                    // Ignore parse errors
                }

                return acc;
            }, {});

            const results = Object.values(groupedUsers).map(u => ({
                ...u,
                columns: Array.from(u.columns)
            }));

            setBoardUsers(results);
        } catch (error) {
            console.error('Error fetching board details:', error);
            toast.error('Failed to fetch user list for this board');
        } finally {
            setDetailLoader(false);
        }
    };

    const confirmDelete = (user) => {
        setDeleteInfo({
            assignmentIds: user.assignmentIds,
            userName: user.name
        });
        setShowDeletePopup(true);
    };

    const handleDeleteAssignment = async () => {
        if (!deleteInfo) return;

        setShowDeletePopup(false);
        const { assignmentIds, userName } = deleteInfo;

        try {
            // Delete all associated assignment records for this user on this board
            await Promise.all(assignmentIds.map(id => services.userassignboard.DELETE_UserAssignBoard(id)));

            toast.success(`${userName} removed from board successfully`);
            // Refresh details
            fetchBoardDetails(selectedBoard);
            // Also refresh counts in background
            fetchBoardSummary();
        } catch (error) {
            console.error('Error deleting assignment:', error);
            toast.error('Failed to remove user from board');
        } finally {
            setDeleteInfo(null);
        }
    };

    useEffect(() => {
        fetchBoardSummary();
    }, []);

    return (
        <div className="user-assign-board-container" style={{ padding: '0', background: '#f4f7f9', height: '100vh', overflowY: 'auto' }}>
            <div className="w-100 px-4 py-4">
                {/* Header */}
                <div className="d-flex align-items-center mb-4 gap-4">
                    <button
                        className="btn btn-light shadow-sm border px-3"
                        style={{ borderRadius: '8px' }}
                        onClick={() => selectedBoard ? setSelectedBoard(null) : navigate(-1)}
                    >
                        <BiArrowBack className="mr-1" />
                        {/* {selectedBoard ? 'Back to Boards' : 'Back'} */}
                    </button>
                    <h1 className="h3 m-0 font-weight-bold text-dark">
                        {selectedBoard ? `Manage: ${selectedBoard.name}` : 'Board Allocation Management'}
                    </h1>
                </div>

                {!selectedBoard ? (
                    /* Dashboard View */
                    <div className="row">
                        {loader ? (
                            <div className="col-12 text-center py-5">
                                <div className="spinner-border text-primary" role="status"></div>
                                <p className="mt-3 text-muted font-weight-medium">Analyzing Board Allocations...</p>
                            </div>
                        ) : boardData.map((board) => (
                            <div key={board.id} className="col-12 col-md-6 col-lg-4 col-xl-3 mb-4">
                                <div className="card h-100 border-0 shadow-sm transition-all hover-transform" style={{ borderRadius: '12px' }}>
                                    <div className="card-body d-flex flex-column p-4">
                                        <div className="d-flex align-items-center mb-3">
                                            <div className="bg-primary-light p-2 rounded mr-3">
                                                <BiLayout className="text-primary h4 m-0" />
                                            </div>
                                            <h5 className="card-title m-0 text-truncate font-weight-bold" title={board.name}>
                                                {board.name}
                                            </h5>
                                        </div>
                                        <div className="mt-auto">
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <span className="text-muted small">Allocated Users</span>
                                                <span className="badge badge-primary badge-pill px-3 py-2" style={{ fontSize: '13px' }}>
                                                    {board.userCount}
                                                </span>
                                            </div>
                                            <button
                                                className="btn btn-primary btn-block"
                                                style={{ borderRadius: '8px' }}
                                                onClick={() => fetchBoardDetails(board)}
                                            >
                                                Manage Users
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    /* Detail User View */
                    <div className="card border-0 shadow-sm" style={{ borderRadius: '15px', overflow: 'hidden' }}>
                        <div className="table-responsive">
                            <table className="table mb-0 table-borderless">
                                <thead className="bg-[#4e73df] text-white">
                                    <tr>
                                        <th className="px-4 py-4 text-center" style={{ width: '80px', fontSize: '12px' }}>#</th>
                                        <th className="px-4 py-4" style={{ width: '300px', fontSize: '12px' }}>ASSIGNED USER</th>
                                        <th className="px-4 py-4" style={{ fontSize: '12px' }}>VISIBLE COLUMNS</th>
                                        <th className="px-4 py-4 text-center" style={{ width: '120px', fontSize: '12px' }}>ACTIONS</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white">
                                    {detailLoader ? (
                                        <tr>
                                            <td colSpan="4" className="py-5 text-center">
                                                <div className="spinner-border text-primary" role="status"></div>
                                                <p className="mt-2 text-muted">Fetching user list...</p>
                                            </td>
                                        </tr>
                                    ) : boardUsers.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="py-5 text-center text-muted italic">No users assigned to this board yet.</td>
                                        </tr>
                                    ) : (
                                        boardUsers.map((user, index) => (
                                            <tr key={user.assignmentId} className="border-bottom hover-light">
                                                <td className="px-4 py-4 text-center">
                                                    <span className="text-muted font-weight-bold">{index + 1}</span>
                                                </td>
                                                <td className="px-4 py-4">
                                                    <div className="d-flex align-items-center">
                                                        <div className="bg-light rounded-circle p-2 mr-3">
                                                            <BiUser className="text-secondary" />
                                                        </div>
                                                        <div className="d-flex flex-column">
                                                            <span className="font-weight-bold text-dark">{user.name}</span>
                                                            <span className="text-secondary small">{user.email}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4">
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {user.columns.length > 0 ? user.columns.map((col, j) => (
                                                            <span key={j} className="badge bg-[#799bf2] text-[#4e73df] border-0 px-2 py-1" style={{ fontSize: '11px', fontWeight: '600' }}>
                                                                {col}
                                                            </span>
                                                        )) : <span className="text-muted small italic">No specific columns</span>}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4 text-center">
                                                    <button
                                                        className="btn btn-outline-danger btn-sm p-2"
                                                        title="Remove user from board"
                                                        onClick={() => confirmDelete(user)}
                                                    >
                                                        <BiTrash className="h5 m-0" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* Custom Modal Popup */}
            <DeletePopup
                open={showDeletePopup}
                title={`Remove ${deleteInfo?.userName} from board?`}
                description="This will delete all assignment records for this user on this specific board."
                onConfirm={handleDeleteAssignment}
                onCancel={() => {
                    setShowDeletePopup(false);
                    setDeleteInfo(null);
                }}
            />

            <style dangerouslySetInnerHTML={{
                __html: `
                .bg-primary-light { background: #eef1ff; }
                .hover-transform:hover { transform: translateY(-5px); box-shadow: 0 .5rem 1rem rgba(0,0,0,.15)!important; }
                .hover-light:hover { background-color: #fcfdfe; }
                .gap-2 { gap: 0.5rem; }
                .transition-all { transition: all 0.3s ease; }
                .font-weight-bold { font-weight: 700!important; }
                .font-weight-medium { font-weight: 500!important; }
            ` }} />
        </div>
    );
};

export default BoardAllocation;
